import { useEffect, useRef } from "react";
import type { VirtuosoHandle } from "react-virtuoso";

declare global {
  interface Window {
    __tableRowScrollInProgress?: boolean;
  }
}

// Virtuoso's scrollToIndex uses average measured row height to estimate the
// scroll offset for unrendered items. When the Virtuoso container is entirely
// off-screen (e.g. a period section below the fold), it has zero measurements
// and estimation produces a garbage offset — typically scrolling to position 0.
//
// Strategy:
//   1. Scroll the container element into view so Virtuoso renders its first
//      batch of items and gets real row-height samples.
//   2. Then call scrollToIndex; retries cover the remaining measurement lag.
//   3. scrollIntoView on the selected row element (in TableRow/MobileCardRow)
//      corrects any remaining estimation error for non-uniform row heights, and
//      flips rowInViewRef so remaining retries here stop firing — otherwise this
//      hook's own estimate-based retries and the row's precise correction land at
//      different offsets and visibly fight each other.
const RAF_FRAME_LIMIT = 60; // ~1s at 60fps waiting for ref to attach
// 5 retries with increasing spacing (total window: ~800ms).
const SCROLL_RETRY_DELAYS_MS = [0, 150, 300, 500, 800];

export type VirtuosoScrollToIndexResult = {
  virtuosoRef: React.RefObject<VirtuosoHandle | null>;
  // Set by the target row itself once it has corrected its own scroll position
  // (see TableRow/MobileCardRow). Checked before each retry so this hook's estimated
  // scrollToIndex calls stop fighting the row's own precise scrollIntoView correction.
  // Structure: { index: number, timestamp: number } to disambiguate stale signals
  // from prior selections vs. the current one.
  rowInViewRef: React.RefObject<{ index: number; timestamp: number } | null>;
};

export const useVirtuosoScrollToIndex = (
  scrollToIndex: number | undefined,
  containerRef: React.RefObject<HTMLElement | null>,
): VirtuosoScrollToIndexResult => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const rowInViewRef = useRef<{ index: number; timestamp: number } | null>(null);

  useEffect(() => {
    if (scrollToIndex === undefined || scrollToIndex < 0) return;

    // React fires child effects before parent effects in the same commit: if the
    // target row is already mounted, TableRow/MobileCardRow's own effect has
    // already run and positioned it correctly by the time this runs. Don't clobber
    // that with an unconditional container scroll — only prime measurements (the
    // reason for this container scroll — see the strategy note above) when the row
    // hasn't already handled its own positioning.
    // Only honor the signal if it's from THIS scrollToIndex call, not a stale
    // signal from a prior rapid selection.
    const rowAlreadyPositioned = rowInViewRef.current !== null && rowInViewRef.current.index === scrollToIndex;

    // Clear the flag for the next scroll cycle.
    rowInViewRef.current = null;

    if (rowAlreadyPositioned) return;

    // Marks the whole coordinated scroll (this hook's retries + the row's own
    // correction) as in progress, so other code (e.g. sort-to-top handlers) doesn't
    // fire a competing window.scrollTo while this settles.
    window.__tableRowScrollInProgress = true;

    // Scroll the container into view first so Virtuoso can measure row heights.
    containerRef.current?.scrollIntoView({ block: "start", behavior: "instant" });

    let cancelled = false;
    let rafFrames = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    // "center" is intentional even for index 0: the browser clamps the scroll
    // position rather than overscrolling, which still leaves the row visible below
    // the page's own header content. "start" would align the row's top edge with
    // the window scroll top, which lands it directly behind the sticky nav bar.
    const align = "center";

    const settle = (): void => {
      if (cancelled) return;
      cancelled = true;
      timers.forEach(clearTimeout);
      window.__tableRowScrollInProgress = false;
    };

    const scheduleScrollRetries = (ref: VirtuosoHandle): void => {
      SCROLL_RETRY_DELAYS_MS.forEach((delay) => {
        const id = setTimeout(() => {
          if (cancelled) return;
          // Only stop retrying if the row positioned THIS scrollToIndex, not a stale signal.
          if (rowInViewRef.current !== null && rowInViewRef.current.index === scrollToIndex) {
            settle();
            return;
          }
          ref.scrollToIndex({ index: scrollToIndex, behavior: "auto", align });
        }, delay);
        timers.push(id);
      });
      // Final retry has fired (or been skipped) — nothing left to coordinate.
      const lastDelay = SCROLL_RETRY_DELAYS_MS[SCROLL_RETRY_DELAYS_MS.length - 1];
      timers.push(setTimeout(settle, lastDelay));
    };

    const waitForRef = (): void => {
      if (cancelled) return;
      rafFrames += 1;
      if (rafFrames > RAF_FRAME_LIMIT) {
        settle();
        return;
      }
      const ref = virtuosoRef.current;
      if (ref) {
        scheduleScrollRetries(ref);
      } else {
        requestAnimationFrame(waitForRef);
      }
    };

    waitForRef();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      window.__tableRowScrollInProgress = false;
    };
  }, [scrollToIndex, containerRef]);

  return { virtuosoRef, rowInViewRef };
};
